// components/ArtistTagSelector.tsx
import { FC, useState, useEffect } from 'react';
import axios from 'axios';
import { Tag } from '../types/tags';

interface ArtistTagSelectorProps {
  artistId?: number;
  albumId?: number;
  accessToken?: string;
  initialTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  onArtistResolved?: (artistId: number) => void;
}

const ArtistTagSelector: FC<ArtistTagSelectorProps> = ({
                                                         artistId,
                                                         albumId,
                                                         accessToken,
                                                         initialTags,
                                                         onTagsChange,
                                                         onArtistResolved
                                                       }) => {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>(initialTags);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resolvedArtistId, setResolvedArtistId] = useState<number | undefined>(artistId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch all available tags
    const fetchTags = async () => {
      try {
        const response = await axios.get<Tag[]>('/api/tags');
        setAllTags(response.data);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    setSelectedTags(initialTags);
  }, [initialTags]);

  useEffect(() => {
    setResolvedArtistId(artistId);
  }, [artistId]);

  useEffect(() => {
    if (resolvedArtistId || !albumId || !accessToken) return;
    const resolveArtist = async () => {
      setIsLoading(true);
      try {
        const response = await axios.post<{ artist_id: number; tags: Tag[] }>(`/api/albums/${albumId}/artist`, { access_token: accessToken });
        setResolvedArtistId(response.data.artist_id);
        onArtistResolved?.(response.data.artist_id);
        setSelectedTags(response.data.tags);
        onTagsChange(response.data.tags);
        setError(null);
      } catch (resolveError) {
        console.error('Error resolving album artist:', resolveError);
        setError('Could not load artist tags.');
      } finally {
        setIsLoading(false);
      }
    };
    void resolveArtist();
  }, [resolvedArtistId, albumId, accessToken, onTagsChange, onArtistResolved]);

  const handleToggleTag = async (tag: Tag) => {
    if (!resolvedArtistId) return;
    setIsLoading(true);
    try {
      const isCurrentlySelected = selectedTags.some(t => t.id === tag.id);
      let newSelectedTags: Tag[];

      if (isCurrentlySelected) {
        // Remove tag
        newSelectedTags = selectedTags.filter(t => t.id !== tag.id);
      } else {
        // Add tag
        newSelectedTags = [...selectedTags, tag];
      }

      // Update the backend
      const tagIds = newSelectedTags.map(t => t.id);
      await axios.put(`/api/artists/${resolvedArtistId}/tags`, { tag_ids: tagIds });

      setSelectedTags(newSelectedTags);
      onTagsChange(newSelectedTags);
    } catch (error) {
      console.error('Error updating tags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <div className="space-y-2">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {/* Selected Tags */}
        {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedTags.map(tag => (
                  <span
                      key={tag.id}
                      className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full flex items-center gap-1 cursor-pointer hover:bg-blue-200"
                      onClick={() => !isLoading && handleToggleTag(tag)}
                  >
              {tag.name}
                    <span className="text-blue-600">×</span>
            </span>
              ))}
            </div>
        )}

        {/* Available Tags */}
        <div className="text-sm text-gray-600 mb-1">Available tags:</div>
        <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
          {allTags
          .filter(tag => !selectedTags.some(selected => selected.id === tag.id))
          .map(tag => (
              <span
                  key={tag.id}
                  className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded-full cursor-pointer hover:bg-gray-200"
                  onClick={() => !isLoading && handleToggleTag(tag)}
              >
              + {tag.name}
            </span>
          ))}
        </div>

        {isLoading && (
            <div className="text-sm text-gray-500">Updating tags...</div>
        )}
      </div>
  );
};

export default ArtistTagSelector;
